import { Component, OnInit } from '@angular/core';
import { SharedSendMoneyModule } from '../components/send-money/shared-send-money.module';
import { SharedSendMoneyComponent } from '../components/send-money/shared-send-money.component';
import { BeneficiarySelectionComponent } from './components/beneficiary-selection/beneficiary-selection.component';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ApiResponseError, DeepPartial, ICustomerSearchRequest, IFormGroupSchema } from '@lib/interfaces';
import { AdvancedSearchModalComponent } from './components/advanced-search-modal/advanced-search-modal.component';
import { SUPPRESS_EVENT } from '@lib/constants';
import { TColumnDefaultSort, SortDirection } from '@lib/modules/table/components';
import { FilterOperator } from '@lib/enums';
import { AngularMaterialModule } from 'src/app/material.module';
import { DateRangePickerComponent } from '@lib/components/date-range-picker/date-range-picker.component';
import { EnterVoucherCodeComponent } from '@pages/home/components/neo-cash-out/enter-voucher-code/enter-voucher-code.component';
import { NEO_CASH_OUT_PANEL_MIN_WIDTH_PX } from '@lib/interfaces/card-less.interface';

type ISearchCustomerForm = IFormGroupSchema<DeepPartial<ICustomerSearchRequest>>;

@Component({
    standalone: true,
    selector: 'app-send-money',
    templateUrl: './send-money.component.html',
    styleUrls: ['./send-money.component.scss'],
    imports: [SharedSendMoneyModule, AngularMaterialModule, FormsModule, ReactiveFormsModule, DateRangePickerComponent],
})
export class SendMoneyComponent extends SharedSendMoneyComponent implements OnInit {
    public override ngOnInit(): void {
        super.ngOnInit();
        this.columns = [
            { transaction_timestamp: 'Date Transferred' },
            { transaction_type: 'Type' },
            { sender_name: 'Sender' },
            { receiver_name: 'Receiver' },
            { amount_received: 'Amount Received' },
            { transfer_status: 'Transfer Status' },
            { transfer_type: 'Transfer Type' },
            { sender_is_super_agent: 'Sender Is Super Agent' },
        ];
        this.defaultColumns = ['transaction_timestamp', 'transaction_type', 'sender_name', 'receiver_name', 'amount_received', 'transfer_status', 'transfer_type', 'sender_is_super_agent'];
        this.redirectToComponent = BeneficiarySelectionComponent;
    }

    protected defaultSort: TColumnDefaultSort = { key: 'created', sort: SortDirection.DESC };
    protected customerSearchPayload: FormGroup<ISearchCustomerForm> = this.createCustomerSearchPayload();
    protected userIds: string[];
    protected isDateFilterApplied: boolean;
    protected visibleColumns: string[] = [];

    onDateChange(event: any) {
        if (event?.startDate && event?.endDate) {
            this.isDateFilterApplied = true;

            const startDate = event.startDate.toDate().toISOString();
            const endDate = event.endDate.toDate().toISOString();

            this.transactionSearchService.removeFilter(this.searchPayload, 'transaction_timestamp', FilterOperator.GREATER_OR_EQUAL, false);
            this.transactionSearchService.removeFilter(this.searchPayload, 'transaction_timestamp', FilterOperator.LESS_OR_EQUAL, false);

            this.filters = [
                {
                    field: 'transaction_timestamp',
                    operator: FilterOperator.GREATER_OR_EQUAL,
                    values: [startDate],
                },
                {
                    field: 'transaction_timestamp',
                    operator: FilterOperator.LESS_OR_EQUAL,
                    values: [endDate],
                },
            ];
            for (const { field, operator, values } of this.filters) {
                this.transactionSearchService.addFilter(this.searchPayload, field, operator, values, false);
            }

            this.transactions = [];
            this.reFetchTransactions();
        } else {
            this.transactionSearchService.removeFilter(this.searchPayload, 'transaction_timestamp', FilterOperator.GREATER_OR_EQUAL, false);
            this.transactionSearchService.removeFilter(this.searchPayload, 'transaction_timestamp', FilterOperator.LESS_OR_EQUAL, false);

            this.transactions = [];
            this.reFetchTransactions();
        }
    }

    downloadFile(data: Blob) {
        const blob = new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = window.URL.createObjectURL(blob);
        window.open(url);
    }

    exportExcel() {
        const snackBarRef = this.matSnackBar.open('Download in Progress', 'Close', { duration: 7000, panelClass: ['snackbar', 'snackbar-success'] });

        let selected_columns: string[] = this.visibleColumns.length > 0 ? this.visibleColumns : this.defaultColumns;
        selected_columns = selected_columns.map((col) => (col === 'transaction_timestamp' ? 'transaction_time' : col));

        const idx = selected_columns.indexOf('amount_received');
        selected_columns = [...selected_columns.slice(0, idx), 'transaction_currency', ...selected_columns.slice(idx)];

        this.transactionService
            .exportTransactionExcelTable(
                'CARD',
                {
                    filters: this.searchPayload.controls.filters?.value,
                    boolean_query: this.searchPayload.controls.boolean_query?.value,
                },
                selected_columns,
            )
            .subscribe({
                next: (res: Blob) => {
                    snackBarRef.dismiss();
                    this.downloadFile(res);
                },
                error: (error: ApiResponseError) => {
                    this.createApiErrorSnackBar(this.matSnackBar, error);
                },
            });
    }

    handleVisibleColumnsChange(columns: string[]): void {
        this.visibleColumns = columns;
    }

    protected isUserFilterApplied(customerFilterField: string): boolean {
        const customerSearchValue = this.customerSearchPayload.value as any;
        return customerSearchValue[`${customerFilterField}_mobile`] || customerSearchValue[`${customerFilterField}_email`];
    }

    protected openAdvanceSearchUserPopup(customerFilterField: string) {
        const customerSearchValue = this.customerSearchPayload.value as any;
        const dialogRef = this.dialog.open(AdvancedSearchModalComponent, {
            autoFocus: false,
            width: '550px',
            data: {
                title: customerFilterField === 'receiver_user' ? 'Receiver' : 'Sender',
                filterResult: {
                    mobile: customerSearchValue[`${customerFilterField}_mobile`],
                    email: customerSearchValue[`${customerFilterField}_email`],
                },
            },
        });

        dialogRef.afterClosed().subscribe((result: ICustomerSearchRequest) => {
            if (!result) return;

            if (result.mobile === '' && result.email === '') {
                this.isFilterApplied = false;
                this.customerSearchPayload.patchValue({
                    [`${customerFilterField}_mobile`]: '',
                    [`${customerFilterField}_email`]: '',
                });
                this.loading = true;
                this.reloadTransactionListner(customerFilterField);
            } else {
                this.isFilterApplied = true;
                this.updateSearchParams(customerFilterField, result);
                const { sender_user_email, receiver_user_email, sender_user_mobile, receiver_user_mobile, ...rest } = this.customerSearchPayload.value as any;

                this.customerService
                    .filterCustomers({
                        ...rest,
                        mobile: sender_user_mobile || receiver_user_mobile,
                        email: sender_user_email || receiver_user_email,
                    })
                    .subscribe({
                        next: (res) => {
                            this.userIds = res.data.map((user: { id: string }) => user.id);
                            this.reloadTransactionListner(customerFilterField);
                        },
                        error: (error: ApiResponseError) => {
                            this.createApiErrorSnackBar(this.matSnackBar, error);
                            this.loading = false;
                        },
                    });
            }
        });
    }

    override reloadTransactionListner(customerFilterField?: string) {
        if (!this.isFilterApplied) {
            this.transactions = [];
            this.searchPayload = this.createSearchTransactionFormGroup();
            this.applyBuiltInFiltersSubAgent();
            this.reFetchTransactions();
        } else {
            this.transactions = [];
            this.applyReceiverFilter(customerFilterField, this.userIds);
            this.reFetchTransactions();
        }
    }

    protected applyBuiltInFiltersSubAgent(): void {
        const masterAccountId = localStorage.getItem('masterAccountId')!;

        this.searchPayload.addControl(
            'boolean_query',
            this.transactionSearchService.createBooleanQueryGroup([
                {
                    queryObjectType: 'bool',
                    bool: {
                        should: [
                            { queryObjectType: 'term', fieldName: 'account_id1.keyword', fieldValue: masterAccountId },
                            { queryObjectType: 'term', fieldName: 'account_id2.keyword', fieldValue: masterAccountId },
                        ],
                    },
                },
                {
                    queryObjectType: 'bool',
                    bool: { should: [{ queryObjectType: 'term', fieldName: 'status_code.keyword', fieldValue: '0000' }] },
                },
                {
                    queryObjectType: 'bool',
                    bool: {
                        should: [
                            { queryObjectType: 'term', fieldName: 'transaction_type.keyword', fieldValue: 'WALLET_TRANSFER' },
                            { queryObjectType: 'term', fieldName: 'transaction_type.keyword', fieldValue: 'WALLET_CREDIT' },
                            // NEOIQ-2694: include Cash Out via Voucher Code redemptions alongside
                            // Wallet Transfer/Credit in this listing.
                            { queryObjectType: 'term', fieldName: 'transaction_type.keyword', fieldValue: 'WALLET_CASHOUT' },
                        ],
                    },
                },
            ]),
            SUPPRESS_EVENT,
        );
    }

    protected applyReceiverFilter(customerFilterField = 'receiver_user', userIds: string[]) {
        const existingFilter = this.transactionSearchService.getFilter(this.searchPayload, `${customerFilterField}_id`, FilterOperator.ANY_OF);

        if (existingFilter) {
            this.transactionSearchService.removeFilter(this.searchPayload, `${customerFilterField}_id`, FilterOperator.ANY_OF, false);
            this.transactionSearchService.addFilter(this.searchPayload, `${customerFilterField}_id`, FilterOperator.ANY_OF, userIds, false);
        } else {
            this.transactionSearchService.addFilter(this.searchPayload, `${customerFilterField}_id`, FilterOperator.ANY_OF, userIds, false);
        }
    }

    protected updateSearchParams(customerFilterField = 'receiver_user', result: ICustomerSearchRequest) {
        const lastNonEmptyKey = Object.keys(result).find((key) => result[key as keyof ICustomerSearchRequest]) || null;

        if (lastNonEmptyKey) this.isFilterApplied = true;

        this.customerSearchPayload.removeControl(`${customerFilterField}_email`, SUPPRESS_EVENT);
        this.customerSearchPayload.removeControl(`${customerFilterField}_mobile`, { emitEvent: !lastNonEmptyKey });

        this.updateFormControl(`${customerFilterField}_email`, result?.email, lastNonEmptyKey);
        this.updateFormControl(`${customerFilterField}_mobile`, result?.mobile, lastNonEmptyKey);
    }

    private createCustomerSearchPayload(): FormGroup<ISearchCustomerForm> {
        return this.formBuilder.group<ISearchCustomerForm>({
            sort_by: this.formBuilder.nonNullable.control(this.defaultSort.key),
            order_by: this.formBuilder.nonNullable.control(this.defaultSort.sort),
        });
    }

    protected updateFormControl(controlName: string, controlValue: string, lastNonEmptyKey: string | null): void {
        if (controlValue && lastNonEmptyKey) {
            this.customerSearchPayload.addControl(controlName, this.formBuilder.nonNullable.control(controlValue), { emitEvent: lastNonEmptyKey === controlName });
        }
    }

    // NEOIQ-2694/2700: "Cash Out for Client" from the Wallet Transfer transaction listing — same
    // entry point as neo-home.component.ts's Home-screen button.
    protected openCashOut(): void {
        const dialogRef = this.sidePanel.open(EnterVoucherCodeComponent, {}, NEO_CASH_OUT_PANEL_MIN_WIDTH_PX);
        dialogRef.componentInstance.agentAccountId = localStorage.getItem('masterAccountId')!;
    }

    removeAdvanceSearch(customerFilterField = 'receiver_user', event: Event) {
        event.stopPropagation();

        this.transactionSearchService.removeFilter(this.searchPayload, `${customerFilterField}_id`, FilterOperator.ANY_OF, true);
        this.customerSearchPayload.patchValue({ [`${customerFilterField}_email`]: '', [`${customerFilterField}_mobile`]: '' });
        this.loading = true;

        this.transactions = [];
        this.reFetchTransactions();
    }
}
